import { captureException } from "@sentry/react";
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { toast } from "react-toastify";
import { regionKey } from "utils/regionKey";
import { fetchLidarClearanceContext } from "./api";
import type {
  LidarClearanceAxis,
  LidarClearanceColorScale,
  LidarClearanceContextData,
  LidarClearanceSystem,
  LidarClearanceSystemAxis,
  LidarType,
} from "./schemas";
import t from "./translations";

const EMPTY_CONTEXT: LidarClearanceContextData = {
  region_tags: [],
  systems: [],
  axes: [],
  system_axes: [],
  color_scales: [],
  lidar_types: [],
};

export interface SystemAxisLink {
  link: LidarClearanceSystemAxis;
  axis: LidarClearanceAxis;
}

interface LidarClearanceContextValue {
  regionTags: string[];
  systems: LidarClearanceSystem[];
  axes: LidarClearanceAxis[];
  systemAxes: LidarClearanceSystemAxis[];
  colorScales: LidarClearanceColorScale[];
  // Every region's point-classification codes, tagged by region.
  lidarTypes: LidarType[];
  // Every axis link (plus its resolved axis) for a system - what the
  // Systems tab's table (axis-name chips) and edit drawer (list/edit/unlink)
  // both need.
  systemAxisLinksBySystem: Map<string, SystemAxisLink[]>;
  // A link only carries system_id - resolves it to the actual system for
  // display (name, customer) wherever a link is shown outside its own
  // system's drawer, e.g. the Color Scales tab's "used in systems" list.
  systemsByRegionId: Map<string, LidarClearanceSystem>;
  // Looked up by an axis's own color_scale (or a system_axes link's
  // override) - what the Axes tab needs.
  colorScaleByKey: Map<string, LidarClearanceColorScale>;
  // Axes whose own default color_scale is a given scale - what the Color
  // Scales tab's details drawer needs to list as "used as default color".
  axesByColorScaleKey: Map<string, LidarClearanceAxis[]>;
  // System-axis links whose own override color_scale is a given scale -
  // the Color Scales tab's details drawer's "used in systems" counterpart
  // to axesByColorScaleKey above, for the system-level override relationship.
  systemAxisLinksByColorScaleKey: Map<string, SystemAxisLink[]>;
  loading: boolean;
  // Callers own their own mutation calls (via ./api) and call this
  // afterward - mirrors DatasetDetailContext's convention of exposing a
  // refresh function rather than a wrapper per mutation.
  refetch: () => Promise<void>;
}

const LidarClearanceContext = createContext<LidarClearanceContextValue | null>(
  null
);

export function LidarClearanceProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<LidarClearanceContextData>(EMPTY_CONTEXT);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await fetchLidarClearanceContext());
    } catch (error) {
      captureException(error, {
        tags: { feature: "lidarClearance.fetchContext" },
      });
      toast.error(t.FailedToLoad);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const axesByRegionId = useMemo(() => {
    const map = new Map<string, LidarClearanceAxis>();
    for (const axis of data.axes) {
      map.set(regionKey(axis.region, axis.id), axis);
    }
    return map;
  }, [data.axes]);

  const systemsByRegionId = useMemo(() => {
    const map = new Map<string, LidarClearanceSystem>();
    for (const system of data.systems) {
      map.set(regionKey(system.region, system.id), system);
    }
    return map;
  }, [data.systems]);

  const systemAxisLinksBySystem = useMemo(() => {
    const map = new Map<string, SystemAxisLink[]>();
    for (const link of data.system_axes) {
      const axis = axesByRegionId.get(regionKey(link.region, link.axis_id));
      if (!axis) continue;
      const key = regionKey(link.region, link.system_id);
      const entry: SystemAxisLink = { link, axis };
      const existing = map.get(key);
      if (existing) existing.push(entry);
      else map.set(key, [entry]);
    }
    return map;
  }, [data.system_axes, axesByRegionId]);

  const colorScaleByKey = useMemo(() => {
    const map = new Map<string, LidarClearanceColorScale>();
    for (const scale of data.color_scales) {
      map.set(regionKey(scale.region, scale.color_scale_id), scale);
    }
    return map;
  }, [data.color_scales]);

  const axesByColorScaleKey = useMemo(() => {
    const map = new Map<string, LidarClearanceAxis[]>();
    for (const axis of data.axes) {
      const key = regionKey(axis.region, axis.color_scale);
      const existing = map.get(key);
      if (existing) existing.push(axis);
      else map.set(key, [axis]);
    }
    return map;
  }, [data.axes]);

  const systemAxisLinksByColorScaleKey = useMemo(() => {
    const map = new Map<string, SystemAxisLink[]>();
    for (const [, links] of systemAxisLinksBySystem) {
      for (const entry of links) {
        if (entry.link.color_scale === null) continue;
        const key = regionKey(entry.link.region, entry.link.color_scale);
        const existing = map.get(key);
        if (existing) existing.push(entry);
        else map.set(key, [entry]);
      }
    }
    return map;
  }, [systemAxisLinksBySystem]);

  return (
    <LidarClearanceContext.Provider
      value={{
        regionTags: data.region_tags,
        systems: data.systems,
        axes: data.axes,
        systemAxes: data.system_axes,
        colorScales: data.color_scales,
        lidarTypes: data.lidar_types,
        systemAxisLinksBySystem,
        systemsByRegionId,
        colorScaleByKey,
        axesByColorScaleKey,
        systemAxisLinksByColorScaleKey,
        loading,
        refetch: load,
      }}
    >
      {children}
    </LidarClearanceContext.Provider>
  );
}

export function useLidarClearance(): LidarClearanceContextValue {
  const ctx = useContext(LidarClearanceContext);
  if (!ctx) {
    throw new Error(
      "useLidarClearance must be used within LidarClearanceProvider"
    );
  }
  return ctx;
}
